import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Field, withFormik } from 'formik';
import { userRolesPropShape } from 'AppSrc/core/auth/auth-proptypes';
import { getDisplayOptionsToUse } from 'AppSrc/core/components/forms/data/get-display-options-to-use';
import {
  VF_MODAL,
} from 'AppSrc/core/components/forms/form-base-components/v-f-modal';
import {
  selectNetworkParamsId,
} from 'AppSrc/core/components/forms/form-types/create-crq/section-one/section-one-constants';
import {
  CHANGE_TYPE,
} from 'AppSrc/core/components/forms/form-types/create-crq/form-definition/section-constants';
import {
  ES_RAISING_EDIT_FROM_SUMMARY,
  ES_VIEWING_EDIT_SECTION,
} from 'AppSrc/core/components/forms/form-base-components/data-structure/constants';
import { InfoIconWorkinfo } from 'AppSrc/core/components/forms/form-types/create-crq/section-change-request-summary/info-icon-workinfo';
import { InfoIconWorkinfoRemedy } from 'AppSrc/core/components/forms/form-types/create-crq/section-change-request-summary/info-icon-remedy';
import {
  currentSelectedNetwork,
} from 'AppSrc/core/components/forms/form-types/create-crq/common-logic';
import { REMEDY_UPLOAD } from 'AppSrc/core/components/forms/form-types/create-crq/section-five/work-information-items/work-information-item-constants';
import {
  SectionNav,
  navigationPropsShape,
} from './section-nav';
import {
  ModalOptions,
  modalOptionsShape,
} from './modal-options';
import {
  SectionError,
} from './section-error';
import {
  formDataShape,
} from './form-base-components/data-structure/v-f-proptypes';
// utilite functions which bridge a bit of a gap between what Formik offers and what we want
import {
  // object mapping strings to components for each form field type
  formFieldTypes,
  // function that returns an object with each field and that field's errors
  validateFormFields,
  // function to tell the user if the form is in a submittable state
  isSectionReadyToSubmit,
  // Renders a bit at the top of the form saying there are errors
  inlineErrorsAreViewable,
  // function to format the data so it's usable in various places
  createFieldDataObjAndArray,
  // gets the initial values for the form based on getCurrentFields
  getInitialValues,
} from './form-section-utility-functions';
import {
  formikValuesShape,
  formikTouchedShape,
} from './form-base-components/data-structure/formik-proptypes';
// we use common logic all over the show and it needs access to some sections, which upsets lint
// import { currentSelectedNetwork } from './form-types/create-crq/common-logic';


// replaces existing navigation props with ones passed in from formSection
// (if a navigationProp is falsey, then it does nothing)
const overwriteNavigationProps = (params) => {
  const {
    navigationProps,
    next,
    prev,
  } = params;

  if (!navigationProps) {
    return null;
  }

  return ({
    ...navigationProps,
    // does formiks handleSubmit first to check for errors, before saving in redux, and then navigates
    next: navigationProps.next ? next : navigationProps.next,
    // saves to redux and then goes to prev
    prev: navigationProps.prev ? prev : navigationProps.prev,
  });
};


const saveDataToRedux = (params) => {
  const {
    handleSuccessfulSubmit,
    shouldAutoNav,
    getCurrentFields,
    sectionTitle,
    sectionId,
    values,
    formData,
    outerFormData,
    userRoles,
  } = params;
  // adding extra text field in summary section (More than 3.5MB)
  if (values.check && !values.summary.includes(REMEDY_UPLOAD)) {
    values.summary = `${REMEDY_UPLOAD}${values.summary}`;
  }
  if (!values.check && values.summary) {
    values.summary = values.summary.includes(REMEDY_UPLOAD) ? values.summary.replace(REMEDY_UPLOAD, '') : values.summary;
  }
  const currentFields = getCurrentFields({
    values,
    formData,
    outerFormData,
    userRoles,
  });
  // FN: 25/03/2020
  // Finding the selected network can be quite difficult. This is how we find it:
  // 1. If the section is CHANGE_TYPE, we get the network immediatly from it's values
  // 2. If the form is modal, then we look in outerFormData for the selected network
  // 3. If the form is a normal form, we just look for the selected network in formData
  const selectedNetworkFromValues = (sectionId === CHANGE_TYPE) ? values[selectNetworkParamsId][0] : null;


  const selectedNetworkFromRedux = outerFormData ? currentSelectedNetwork(outerFormData) : currentSelectedNetwork(formData);

  let selectedNetwork = selectedNetworkFromRedux ? selectedNetworkFromRedux : selectedNetworkFromValues;
  selectedNetwork = ((selectedNetwork === 'fixedOlo') || (selectedNetwork === 'fixedInternational')) ? 'fixed' : selectedNetwork;

  const {
    dataByFieldId,
    fieldIdsByDisplayOrder,
  } = createFieldDataObjAndArray({
    currentFields,
    values,
    selectedNetwork,
  });

  const data = {
    // this is for sending to server
    dataByFieldId,
    // this is to show the user
    fieldIdsByDisplayOrder,
    sectionTitle,
    sectionId,
  };
  handleSuccessfulSubmit(data, shouldAutoNav);
};





const getAllFieldsTouched = (values) => {

  const allFieldKeys = Object.keys(values);
  const allFieldsTouched = {};

  allFieldKeys.forEach((fieldId) => {
    allFieldsTouched[fieldId] = true;
  });

  return allFieldsTouched;
};


class FormSectionDisplay extends PureComponent {
  constructor(props) {
    super(props);
    this.handleSaveToRedux = this.handleSaveToRedux.bind(this);
    this.handleUserSubmit = this.handleUserSubmit.bind(this);
    this.goToPrev = this.goToPrev.bind(this);
    this.formStartRef = React.createRef();
  }

  componentDidMount() {
    // Calls formiks validator
    this.props.validateForm();

    const {
      values,
      editingStatus,
    } = this.props;

    const shouldShowErrors = (editingStatus === ES_RAISING_EDIT_FROM_SUMMARY) || (editingStatus === ES_VIEWING_EDIT_SECTION);

    if (shouldShowErrors) {
      const allFieldsTouched = getAllFieldsTouched(values);
      this.props.setTouched(allFieldsTouched, true);
    }

    // focus at the first non-disabled button element in the form
    // which should be the first active input
    const formStart = this.formStartRef.current;
    if (formStart) {
      formStart.focus();
    }
  }

  handleSaveToRedux(shouldAutoNav) {
    const {
      handleSuccessfulSubmit,
      getCurrentFields,
      sectionTitle,
      sectionId,
      values,
      formData,
      outerFormData,
      userRoles,
    } = this.props;
    saveDataToRedux({
      handleSuccessfulSubmit,
      shouldAutoNav,
      getCurrentFields,
      sectionTitle,
      sectionId,
      values,
      formData,
      outerFormData,
      userRoles,
    });
  }

  handleUserSubmit(e) {
    const {
      handleSubmit,
      allowSubmissionWithErrors,
      saveFormSectionErrors,
      sectionId,
      errors,
      formData,
    } = this.props;
    const statusReason = formData.statusReason;
    if (statusReason === '10500' && sectionId === 'WORK_INFORMATION_ITEMS') {
      window.localStorage.setItem('isInfoItem', true);
    }
    // If we should allow submission with errors, we need to bypass formik's handleSubmit functionality
    // and directly navigate with saveDataToRedux.
    if (allowSubmissionWithErrors) {
      e.preventDefault();
      const shouldAutoNav = true;
      this.handleSaveToRedux(shouldAutoNav);
      // however we also need to save any errors to redux so they will appear on the summary page
      if (!saveFormSectionErrors) {
        console.warn(`Allowing submission with errors for ${sectionId} without supplying a method for saveFormSectionErrors`);
        return;
      }
      // save any errors to redux
      saveFormSectionErrors(sectionId, errors);
      return;
    }

    // FN: 14/04/2020
    // We have to use Formik's handleSubmit to saveData before going to the next step. However handleSubmit does
    // some asynch stuff without returning anything. This means we are unable to navigate outside of handleSubmit.
    // As such the logic works like so:
    // Formik's handleSubmit checks if the form is valid. If it is, it then calls saveDataToRedux which does a load
    // of stuff including calling handleSuccessfulSubmit. handleSuccessfulSubmit comes from the top level component
    // (such as mainForm). It is then handleSuccessfulSubmit that performs the navigation
    handleSubmit(e);
  }

  goToPrev() {
    const shouldAutoNav = false;
    // Saves data to redux
    this.handleSaveToRedux(shouldAutoNav);
    // navigates to previous step
    this.props.navigationProps.prev();
  }

  render() {
    const {
      getCurrentFields,
      sectionTitle,
      sectionId,
      userRoles,
      userGroups,
      formData,
      outerFormData,
      navigationProps,
      modalOptions,
      values,
      touched,
      handleSubmit,
      errors,
      allowSubmissionWithErrors,
    } = this.props;


    const currentFields = getCurrentFields({
      values,
      formData,
      outerFormData,
      touched,
      userRoles,
      userGroups,
      sectionId,
    });

    const readyToSubmit = isSectionReadyToSubmit(errors) || allowSubmissionWithErrors;
    const viewableErrors = inlineErrorsAreViewable(errors, touched);
    const status = formData.status;
    const statusReason = formData.statusReason;


    // adds in the middle functions for next/prev from fomik
    const curriedNavigationProps = overwriteNavigationProps({
      navigationProps,
      next: this.handleUserSubmit,
      prev: this.goToPrev,
    });

    return (
      <div className="form-section">
        <h3 className="form-section-title">
          {sectionTitle}
        </h3>
        <form
          className="formik-form"
        >
          <div>
            { sectionId === 'WORK_INFORMATION_ITEMS' ? <InfoIconWorkinfo /> : ''}
            { sectionId === 'WORK_INFORMATION_ITEMS' ? <InfoIconWorkinfoRemedy /> : ''}
          </div>
          <div
            tabIndex="-1"
            className="hidden-but-can-focus"
            ref={this.formStartRef}
          />
          {
            viewableErrors && (
              <SectionError />
            )
          }
          {
            currentFields.map((field, i) => {
              if (!field.shouldRender) {
                return null;
              }

              const {
                disabledFieldOptions,
                readOnly,
                componentData,
                optional,
                updateValue,
              } = field;

              const {
                id,
                vFParams,
                fieldType,
              } = componentData;


              // modals are technically seperate forms. They have their own version of formData that they maintain. In general
              // modals should act as their own middleware between the actual form and their own form. However this isn't always
              // possible. When that occurs, we have to pass through outerFormData

              const fieldTypeIsModal = fieldType === VF_MODAL;
              const selectedNetwork = currentSelectedNetwork(outerFormData ? outerFormData : formData);

              const displayOptions = getDisplayOptionsToUse({
                componentData,
                selectedNetwork,
              });

              return (
                <Field
                  key={id}
                  name={id}
                  optional={optional}
                  displayOptions={displayOptions}
                  disabledFieldOptions={disabledFieldOptions}
                  readOnly={readOnly}
                  vFParams={vFParams}
                  component={formFieldTypes[fieldType]}
                  updateValue={updateValue}
                  outerFormData={fieldTypeIsModal ? formData : null}
                />
              );
            })
          }
          {/*
            navigationProps and modalOptions ideally shouldn't be here. They take quite a lot of props
            that need passing down from a TLC. However they rely on Formik to handle the checks and it's
            easier to read props being passed down than it is to read callbacks being passed up
          */}
          {
            navigationProps && (
              <SectionNav
                key={sectionId}
                navigationProps={curriedNavigationProps}
                handleSaveToRedux={this.handleSaveToRedux}
                handleSubmit={this.handleUserSubmit}
                readyToSubmit={readyToSubmit}
                sectionData={values}
                sectionId={sectionId}
                statusReason={statusReason}
                status={status}
              />
            )
          }
          {
            modalOptions && (
              <ModalOptions
                modalOptions={modalOptions}
                handleSubmit={handleSubmit}
                readyToSubmit={readyToSubmit}
                values={values}
              />
            )
          }
        </form>
      </div>
    );
  }
}

// extract withFormik parameters into a seperate object, for ease of testing
const formSectionFormikData = {
  // values for the fields on render
  // the withFormik version uses mapPropsToValues instead of initialValues
  // the function takes props and has the same form as getInitialValues
  // so we can just use that.
  mapPropsToValues: getInitialValues,
  // calles the validator on fields requesting validation
  validate: (
    values,
    {
      getCurrentFields,
      formData,
      outerFormData,
      userRoles,
      sectionId,
    },
  ) => {
    // Formik's validate functions does not have any error catching so it silently fails
    // unless we do our own...
    try {
      const currentFields = getCurrentFields({
        values,
        formData,
        outerFormData,
        userRoles,
      });
      return validateFormFields({
        currentFields,
        values,
        userRoles,
        formData,
        outerFormData,
        sectionId,
      });
    }
    catch (codeError) {
      console.error(codeError);
      return null;
    }

  },
  // in withFormik onSubmit becomes handleSubmit
  handleSubmit: (
    values,
    {
      props: {
        handleSuccessfulSubmit,
        getCurrentFields,
        formData,
        outerFormData,
        userRoles,
        sectionTitle,
        sectionId,
      },
      setSubmitting,
    },
  ) => {

    const shouldAutoNav = true;

    saveDataToRedux({
      handleSuccessfulSubmit,
      shouldAutoNav,
      getCurrentFields,
      sectionTitle,
      sectionId,
      values,
      formData,
      outerFormData,
      userRoles,
    });
    setSubmitting(false);
    window.localStorage.setItem('validation', false);
    window.localStorage.setItem('validation1', false);
    window.localStorage.setItem('validation2', false);
  },
};

// Add formik functionality and props to the FormSectionDisplay component
const FormSection = withFormik(formSectionFormikData)(FormSectionDisplay);



FormSectionDisplay.propTypes = {
  handleSuccessfulSubmit: PropTypes.func,
  getCurrentFields: PropTypes.func.isRequired,
  sectionTitle: PropTypes.string,
  sectionId: PropTypes.string,
  userRoles: userRolesPropShape,
  userGroups: PropTypes.objectOf(PropTypes.array),
  formData: formDataShape,
  outerFormData: formDataShape,
  navigationProps: navigationPropsShape,
  modalOptions: modalOptionsShape,
  values: formikValuesShape,
  handleSubmit: PropTypes.func,
  touched: formikTouchedShape,
  validateForm: PropTypes.func,
  errors: PropTypes.objectOf(PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))),
  editingStatus: PropTypes.string,
  setTouched: PropTypes.func,
  allowSubmissionWithErrors: PropTypes.bool,
  saveFormSectionErrors: PropTypes.func,
};

export { FormSection, FormSectionDisplay, formSectionFormikData };
